import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Select } from '../ui/Select';
import searchIcon from '../../assets/images/search.png';
import filterIcon from '../../assets/images/filter.png';
import plusIcon from '../../assets/images/plus.png';
import editIcon from '../../assets/images/edit.png';
import deleteIcon from '../../assets/images/delete.png';
import fileIcon from '../../assets/images/files.png';
import folderIcon from '../../assets/images/folder.png';

interface ListItem {
  id: number | string;
  name: string;
  description?: string;
  category?: string;
  type?: string;
  start_date?: string;
  end_date?: string;
  balance?: number | string
}

interface ListPageTemplateProps {
  title: string;
  description?: string;
  items: ListItem[];
  loading?: boolean;
  onItemClick?: (item: ListItem) => void;
  showSearch?: boolean;
  showDateRange?: boolean;
  emptyMessage?: string;
  emptyActionLabel?: string;
  onEmptyAction?: () => void;
}

const ListPageTemplate: React.FC<ListPageTemplateProps> = ({
  title,
  description,
  items,
  loading = false,
  onItemClick,
  showSearch = true,
  showDateRange = false,
  emptyMessage = 'No items found',
  onEmptyAction
}) => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');

  // Filter items based on search and filters
  const filteredItems = items.filter(item => {
    // Search filter
    if (searchTerm && !item.name.toLowerCase().includes(searchTerm.toLowerCase()) &&
      !item.description?.toLowerCase().includes(searchTerm.toLowerCase())) {
      return false;
    }

    return true;
  });

  const handleItemClick = (item: ListItem, e: React.MouseEvent) => {
    // Don't trigger click if action button was clicked
    if ((e.target as HTMLElement).closest('button')) {
      return;
    }
    if (onItemClick) {
      onItemClick(item);
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch (error) {
      return dateString;
    }
  };

  return (
    <div className="h-shink-from-navbar h-fill-from-navbar px-2 bg-bg_grey_light text-dark overflow-y-scroll">
      <div className="max-w-7xl mx-auto">
        {/* Page Header */}
        <div className="p-6 pb-3 bg-light rounded-t-lg flex flex-col sm:flex-row justify-between items-start sm:items-center mb-2">
          <div className="w-full sm:w-auto">
            <h3 className="text-xl">{title}</h3>
            {description && (
              <p className="text-grey_dark mt-1 text-sm">
                {description}
              </p>
            )}
          </div>

        </div>

        {/* Search and Filter Section */}
        {(showSearch) && (
          <div className="bg-light rounded-lg p-4 mb-4">
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center w-full gap-4">
              {showSearch && (
                <div className="relative w-full sm:w-96">
                  <img
                    src={searchIcon}
                    alt="Search"
                    className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5"
                  />
                  <input
                    type="text"
                    placeholder={`Search ${title.toLowerCase()}...`}
                    className="pl-10 pr-2 py-2 bg-light text-dark placeholder-grey_dark w-full border border-grey_light rounded-lg focus:outline-none focus:border-dark"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
              )}


              <div className="text-sm text-grey_dark sm:ml-auto">
                {filteredItems.length} of {items.length} items
              </div>
            </div>

          </div>
        )}

        {/* Items List */}
        <div className="bg-light rounded-lg p-4 sm:p-6">
          {loading ? (
            <div className="text-center py-8">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              <p className="mt-2 text-grey_dark">Loading {title.toLowerCase()}...</p>
            </div>
          ) : filteredItems.length === 0 ? (
            <div className="text-center py-8">
              <div className="w-16 h-16 mx-auto mb-4 bg-bg_grey_light rounded-full flex items-center justify-center">
                <img
                  src={searchIcon}
                  alt="Search"
                  className="w-8 h-8 opacity-50"
                />
              </div>
              <h4 className="text-lg mb-2">{emptyMessage}</h4>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredItems.map((item) => (
                <div
                  key={item.id}
                  onClick={(e) => handleItemClick(item, e)}
                  className="p-4 bg-light border border-grey_light rounded-lg hover:bg-bg_grey_light hover:border-grey_dark cursor-pointer transition-all duration-200 group"
                >
                  <div className="flex items-start">
                    {/* Icon */}
                    <div className="flex-shrink-0 mr-4">
                      <div className="relative">
                        <img
                          src={fileIcon}
                          alt={item.name}
                          className="w-10 h-10 object-contain"
                        />
                      </div>
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex justify-between items-center">
                        <div>
                          <h5 className="font-medium group-hover:text-dark dark:group-hover:text-primary transition-colors duration-200 truncate">
                            {item.name}
                          </h5>
                          {item.description && (
                            <p className="text-sm text-grey_dark mt-1 line-clamp-2">
                              {item.description}
                            </p>
                          )}
                        </div>

                                                {item.balance && (
                          <p 
                            className="text-grey_dark mx-2 line-clamp-2 dashboard-figure"
                            title={typeof item.balance === 'string' ? item.balance : item.balance?.toString()}
                          >
                            {item.balance}
                          </p>
                        )}

                      </div>

                      {/* Metadata */}
                      <div className="flex flex-wrap gap-2 mt-2 pt-2 border-t border-grey_light">
                        {item.category && (
                          <span className="text-xs px-2 py-1 bg-bg_grey_light text-grey_dark rounded">
                            {item.category}
                          </span>
                        )}
                        {item.type && (
                          <span className="text-xs px-2 py-1 bg-bg_grey_light text-grey_dark rounded">
                            {item.type}
                          </span>
                        )}
                        {showDateRange && item.start_date && item.end_date && (
                          <span className="text-xs px-2 py-1 bg-bg_grey_light text-grey_dark rounded">
                            {formatDate(item.start_date)}
                          </span>
                        )}
                       
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

       
      </div>
    </div>
  );
};

export default ListPageTemplate;